'use server';

import bcrypt from 'bcrypt';

import { logAuditEvent } from '@/lib/audit-log';
import { revokeUserSessions } from '@/lib/auth-revocation';
import { getSession } from '@/lib/auth-session';
import { prisma } from '@/lib/db';
import { sendEmail } from '@/lib/email';
import { resolveMailBranding } from '@/lib/network-settings-branding';
import { hashToken, tokenIdentityMatches } from '@/lib/token-hash';
import { type ActionResult, failure, ok } from '@/lib/types/action-result';

import {
    createPasswordResetToken,
    getMissingSmtpConfig,
    getRequestIp,
    isSmtpConfigured,
    loginResolveLimiter,
    MIN_PASSWORD_LENGTH,
    passwordResetIpLimiter,
    passwordResetLimiter,
    pwProbeLimiter,
    resetTokenIpLimiter,
    resetTokenLimiter,
    safeLogSystemError,
    SALT_ROUNDS,
} from '../users-shared';

export async function changePassword(oldPw: string, newPw: string): Promise<ActionResult> {
    try {
        const session = await getSession();
        if (!session?.user?.email) return failure('Nicht eingeloggt');

        if (!newPw || newPw.length < MIN_PASSWORD_LENGTH) {
            return failure(`Passwort muss mindestens ${MIN_PASSWORD_LENGTH} Zeichen lang sein.`);
        }

        const user = await prisma.user.findUnique({ where: { email: session.user.email } });
        if (!user) return failure('User nicht gefunden');

        // Finding 1: Better Auth (emailAndPassword.enabled) legt Credential-Hashes
        // in account.password ab, nicht zwingend in user.password. Wenn wir die
        // Alt-Passwort-Pruefung nur an user.password knuepfen, koennte ein
        // Angreifer mit gestohlener Session bei einem so angelegten Konto die
        // Abfrage komplett umgehen und das vorhandene Passwort ueberschreiben.
        // Daher den effektiven Hash aus beiden Orten aufloesen (wie in
        // startImpersonation) und die Pruefung dagegen erzwingen.
        const credentialAccount = await prisma.account.findFirst({
            where: { userId: user.id, providerId: 'credential' },
        });
        const effectiveHash = user.password ?? credentialAccount?.password ?? null;

        if (effectiveHash) {
            if (!oldPw) {
                return failure('Bitte geben Sie das aktuelle Passwort ein.');
            }
            const match = await bcrypt.compare(oldPw, effectiveHash);
            if (!match) return failure('Das aktuelle Passwort ist falsch.');
        }

        const hashed = await bcrypt.hash(newPw, SALT_ROUNDS);
        await prisma.user.update({
            where: { id: user.id },
            data: { password: hashed }
        });

        // Ensure credential account exists for Better Auth login
        if (credentialAccount) {
            await prisma.account.update({
                where: { id: credentialAccount.id },
                data: { password: hashed },
            });
        } else {
            await prisma.account.create({
                data: {
                    userId: user.id,
                    accountId: String(user.id),
                    providerId: 'credential',
                    provider: 'credential',
                    providerAccountId: String(user.id),
                    type: 'credential',
                    password: hashed,
                },
            });
        }

        // Review A1: Passwortänderung meldet alle ANDEREN Sessions ab —
        // eine gestohlene Alt-Session überlebt den Passwortwechsel nicht.
        // Die aktuelle Session bleibt gültig (kein erzwungener Re-Login).
        const currentToken = (session as { session?: { token?: string } }).session?.token;
        await revokeUserSessions(
            user.id,
            currentToken ? { exceptToken: currentToken } : undefined,
        );

        await logAuditEvent({
            userId: user.id,
            action: 'user.password_change',
            resourceType: 'User',
            resourceId: user.id,
            metadata: { method: 'self_service' },
        });

        return ok();
    } catch (e) {
        console.error(e);
        void safeLogSystemError('User password change failed', e);
        return failure('Fehler beim Ändern des Passworts');
    }
}

export async function requestPasswordReset(email: string): Promise<ActionResult> {
    try {
        const normalizedEmail = email.trim().toLowerCase();
        if (!normalizedEmail) return failure('Email ist erforderlich');

        const ip = await getRequestIp();
        // Two-key limiter: per-email (5/5min) catches a focused attack on
        // one account, per-IP (20/5min) catches enumeration across many
        // emails which would otherwise bypass the per-email key.
        const [{ success: emailOk }, { success: ipOk }] = await Promise.all([
            passwordResetLimiter.limit(`pw-reset:${normalizedEmail}`),
            passwordResetIpLimiter.limit(`pw-reset-ip:${ip}`),
        ]);
        if (!emailOk || !ipOk) {
            return failure('Zu viele Anfragen. Bitte versuchen Sie es später erneut.');
        }

        if (!isSmtpConfigured()) {
            const missing = getMissingSmtpConfig();
            const missingHint = missing.length > 0 ? ` (fehlt: ${missing.join(', ')})` : '';
            return failure(`SMTP ist nicht konfiguriert${missingHint}`);
        }

        const user = await prisma.user.findUnique({ where: { email: normalizedEmail } });

        // Review 2026-07-28 (Mittel 1, Ausstellungsseite): Für ein gesperrtes
        // oder soft-gelöschtes Konto entsteht kein Token mehr.
        //
        // Der koordinierte E-Mail-Wechsel sperrt das Konto genau deshalb
        // (Runbook Schritt 2). Wer in diesem Fenster das ALTE Postfach
        // besitzt, konnte sich bisher einen zwei Stunden gültigen Link
        // ausstellen lassen und ihn nach der Umstellung einlösen. Die
        // Ausstellung ist dabei die leichtere Hälfte — die Einlösung unten
        // ist die tragende Sperre, weil ein Token auch VOR der Sperre
        // entstanden sein kann.
        //
        // Die Antwort bleibt in jedem Fall ok(): Eine abweichende Antwort
        // machte den Kontozustand für jeden Besucher auslesbar.
        const blockedForIssuing = Boolean(user?.suspendedAt || user?.deletedAt);
        if (user?.email && !blockedForIssuing) {
            const token = await createPasswordResetToken(user.id, user.email);
            const baseUrl = process.env.PORTAL_PUBLIC_URL || process.env.NEXTAUTH_URL || '';
            const resetUrl = `${baseUrl.replace(/\/$/, '')}/reset-password?token=${token}`;
            const branding = await resolveMailBranding({ userId: user.id });
            const emailResult = await sendEmail({
                to: user.email,
                subject: 'Passwort zurücksetzen',
                text: `Sie können Ihr Passwort zurücksetzen: ${resetUrl}`,
                html: `<p>Sie können Ihr Passwort zurücksetzen:</p><p><a href=\"${resetUrl}\">${resetUrl}</a></p>`,
                senderNamePostfix: branding.senderNamePostfix,
                signature: branding.signature,
            });
            // Code-review S-A (2026-05-13): swallow SMTP failure so the
            // existence of the e-mail isn't observable through the
            // distinction "ok vs SMTP-error returns failure". Log
            // server-side so operators see the SMTP issue.
            if (emailResult.error) {
                void safeLogSystemError('Password-reset e-mail SMTP failure', undefined, {
                    smtpError: emailResult.error,
                    // intentional: no email here — redacted by S-E anyway
                });
            }
        }

        // ALWAYS return ok() — the caller cannot distinguish "e-mail
        // unknown" from "e-mail known but SMTP down".
        return ok();
    } catch (e) {
        void safeLogSystemError('Password reset request failed', e);
        // Same generic response on programming errors — leaking
        // implementation hiccups distinguishes account states.
        return ok();
    }
}

/**
 * Typed sentinel used to abort the password-reset $transaction. We must
 * `throw` to roll the transaction back, but the OUTER catch keys off the
 * `code` property (not message string parsing) and converts to a typed
 * `ActionResult` failure with the same code.
 */
class ResetTokenError extends Error {
    constructor(public readonly code: 'TOKEN_INVALID' | 'TOKEN_EXPIRED' | 'TOKEN_BLOCKED') {
        super(code);
        this.name = 'ResetTokenError';
    }
}

export async function resetPasswordWithToken(
    token: string,
    newPassword: string,
): Promise<ActionResult> {
    // Review 2026-07-28: Konto des abgewiesenen Tokens. Ausserhalb des `try`,
    // weil der `catch`-Zweig aufräumt — die Aufräumarbeit muss NACH dem
    // Rollback laufen, sonst nimmt der Abbruch der Transaktion sie wieder mit.
    let blockedUserId: number | null = null;
    let blockedReason: 'account_blocked' | 'identity_changed' | null = null;

    try {
        if (!token || !newPassword) return failure('Ungültige Anfrage', 'INVALID_REQUEST');

        // Code-review L-F (2026-05-13): the token-prefix-only limiter
        // could be defeated by submitting distinct fake tokens — each
        // got its own 5/5-min bucket. Pair with an IP-keyed limiter so
        // distinct tokens from one IP share a budget.
        const ip = await getRequestIp();
        const [{ success: tokenOk }, { success: ipOk }] = await Promise.all([
            resetTokenLimiter.limit(`pw-reset-token:${token.slice(0, 16)}`),
            resetTokenIpLimiter.limit(`pw-reset-token-ip:${ip}`),
        ]);
        if (!tokenOk || !ipOk) return failure('Zu viele Versuche. Bitte später erneut versuchen.', 'RATE_LIMITED');

        if (newPassword.length < MIN_PASSWORD_LENGTH) {
            return failure(
                `Passwort muss mindestens ${MIN_PASSWORD_LENGTH} Zeichen lang sein.`,
                'PASSWORD_TOO_SHORT',
            );
        }

        const hashedPassword = await bcrypt.hash(newPassword, SALT_ROUNDS);

        // Hoist out of the transaction so we can audit-log AFTER commit
        // using the userId we resolved from the consumed token.
        let resetUserId: number | null = null;

        // F6: In der DB steht nur der SHA-256-Hash — den eingehenden
        // Roh-Token hashen und per Hash nachschlagen/löschen.
        const tokenHash = hashToken(token);

        await prisma.$transaction(async (tx) => {
            const resetToken = await tx.passwordResetToken.findUnique({
                where: { token: tokenHash },
            });

            // Throws inside $transaction are how we abort the transaction —
            // we use a typed error class so the outer catch can branch on
            // `instanceof` / `code` instead of parsing `e.message`.
            if (!resetToken) throw new ResetTokenError('TOKEN_INVALID');
            if (resetToken.expiresAt < new Date()) {
                await tx.passwordResetToken.delete({ where: { token: tokenHash } });
                throw new ResetTokenError('TOKEN_EXPIRED');
            }

            // Review 2026-07-28 (Mittel 1 + 3, Einlöseseite). Bis hierher wurde
            // ausschliesslich der Token geladen und blind dessen `userId`
            // aktualisiert — weder Sperrstatus noch aktuelle Adresse spielten
            // eine Rolle. Genau daran hängt die Kontoübernahme über den
            // koordinierten E-Mail-Wechsel (siehe Testdatei
            // reset-token-identity-binding.test.ts).
            const account = await tx.user.findUnique({
                where: { id: resetToken.userId },
                select: { id: true, email: true, suspendedAt: true, deletedAt: true },
            });
            if (!account) throw new ResetTokenError('TOKEN_INVALID');

            // Mittel 1: Während der Sperre wird nicht eingelöst. Das ist das
            // wichtigere Ende — ein vor der Sperre ausgestellter Token käme
            // sonst durch, obwohl die Ausstellung längst blockiert ist.
            if (account.suspendedAt || account.deletedAt) {
                blockedUserId = account.id;
                blockedReason = 'account_blocked';
                throw new ResetTokenError('TOKEN_BLOCKED');
            }

            // Mittel 3: Der Token ist an die Adresse gebunden, gegen die er
            // ausgestellt wurde. Nach einem Adresswechsel passt er nicht mehr
            // und ist wertlos — auch dann, wenn der Betreiber den Widerruf aus
            // Schritt 3 des Runbooks vergessen hat.
            if (!tokenIdentityMatches(token, account.email)) {
                blockedUserId = account.id;
                blockedReason = 'identity_changed';
                throw new ResetTokenError('TOKEN_BLOCKED');
            }

            resetUserId = resetToken.userId;

            await tx.user.update({
                where: { id: resetToken.userId },
                data: { password: hashedPassword },
            });

            // Ensure credential account exists for Better Auth login
            const existing = await tx.account.findFirst({
                where: { userId: resetToken.userId, providerId: 'credential' },
            });
            if (existing) {
                await tx.account.update({
                    where: { id: existing.id },
                    data: { password: hashedPassword },
                });
            } else {
                await tx.account.create({
                    data: {
                        userId: resetToken.userId,
                        accountId: String(resetToken.userId),
                        providerId: 'credential',
                        provider: 'credential',
                        providerAccountId: String(resetToken.userId),
                        type: 'credential',
                        password: hashedPassword,
                    },
                });
            }

            await tx.passwordResetToken.deleteMany({ where: { userId: resetToken.userId } });
        });

        // Review A1: Nach Passwort-Reset alle bestehenden Sessions widerrufen —
        // der Reset ist der "Konto zurückerobern"-Pfad, Altzugriffe müssen sterben.
        if (resetUserId !== null) {
            await revokeUserSessions(resetUserId);
        }

        // Audit OUTSIDE the transaction so an audit-log failure cannot
        // roll back the password change.
        if (resetUserId !== null) {
            try {
                await logAuditEvent({
                    userId: resetUserId,
                    action: 'user.password_reset',
                    resourceType: 'User',
                    resourceId: resetUserId,
                    metadata: { method: 'reset_token' },
                });
            } catch (auditError) {
                void safeLogSystemError('Password reset audit log failed', auditError);
            }
        }

        return ok();
    } catch (e) {
        if (e instanceof ResetTokenError) {
            if (e.code === 'TOKEN_BLOCKED') {
                // Der abgewiesene Token wird verbrannt, statt liegen zu
                // bleiben: Sonst wäre "warten, bis der Betreiber das Konto
                // wieder aktiviert" ein Wiederholungsversuch mit demselben
                // Link. Alle Token dieses Kontos, weil sie dieselbe Herkunft
                // haben — der Nutzer fordert nach der Umstellung einen neuen an.
                if (blockedUserId !== null) {
                    try {
                        await prisma.passwordResetToken.deleteMany({ where: { userId: blockedUserId } });
                    } catch (cleanupError) {
                        void safeLogSystemError('Password reset token cleanup failed', cleanupError);
                    }
                }
                // Sichtbar machen: Ein abgewiesener Link ist entweder ein
                // vergessener Widerruf im koordinierten Wechsel oder ein
                // Übernahmeversuch. Beides gehört ins Systemprotokoll, ohne
                // Adresse oder Token darin.
                void safeLogSystemError('Password reset token rejected', undefined, {
                    reason: blockedReason,
                    userId: blockedUserId,
                });
                // BEWUSST dieselbe Antwort wie bei einem unbekannten Token:
                // Ein eigener Code machte "Konto gesperrt" bzw. "Adresse
                // gewechselt" für den Linkinhaber ablesbar. Wer legitim
                // gesperrt ist, erfährt den Grund beim Anmelden (getLoginProbe
                // liefert dort `suspended`).
                return failure('Token ist ungültig oder abgelaufen', 'TOKEN_INVALID');
            }
            if (e.code === 'TOKEN_INVALID') {
                return failure('Token ist ungültig oder abgelaufen', 'TOKEN_INVALID');
            }
            return failure('Token ist abgelaufen', 'TOKEN_EXPIRED');
        }
        console.error(e);
        void safeLogSystemError('Password reset failed', e);
        return failure('Passwort konnte nicht zurückgesetzt werden', 'INTERNAL_ERROR');
    }
}

// Code-review L-A (2026-05-13): combined post-login probe.
// The old login flow called two separate server actions
// (getEmailVerificationStatus + checkUserSuspended) on every failed
// login — each ran bcrypt and consumed its own rate-limit tokens,
// burning 2-3 probes per failed attempt and tripping the limiter
// prematurely on shared NAT. This single-shot probe runs bcrypt
// once and reports verification + suspension via a discriminated
// union, so the form picks the right message in one round-trip.
export type LoginProbe =
    | { status: 'invalid' }
    | { status: 'verified' }
    | { status: 'suspended' }
    | { status: 'unverified'; tokenExpired: boolean; expiresAt?: string; pending?: boolean };

export async function getLoginProbe(email: string, password?: string): Promise<ActionResult<LoginProbe>> {
    try {
        const normalizedEmail = email.trim().toLowerCase();
        if (!normalizedEmail || !password) return ok({ status: 'invalid' });

        const ip = await getRequestIp();
        const [{ success: emailOk }, { success: ipOk }] = await Promise.all([
            pwProbeLimiter.limit(`pw-probe:${normalizedEmail}`),
            pwProbeLimiter.limit(`pw-probe-ip:${ip}`),
        ]);
        if (!emailOk || !ipOk) return ok({ status: 'invalid' });

        const user = await prisma.user.findFirst({
            where: {
                OR: [
                    { email: normalizedEmail },
                    { pendingEmail: normalizedEmail },
                ],
            },
            select: {
                id: true,
                emailVerified: true,
                pendingEmail: true,
                password: true,
                deletedAt: true,
                suspendedAt: true,
                // Review-Nachfassung 2026-07-25 (Problem 4b): Better-Auth legt
                // das Passwort in der Account-Zeile (providerId='credential')
                // ab; `User.password` kann leer sein, obwohl das Konto ein
                // Passwort hat. Die Probe sah solche Konten als "kein Passwort"
                // und antwortete `invalid` — ein unbestätigtes Konto bekam
                // damit die generische Fehlermeldung statt des
                // Wiederversand-Angebots. Gleiche Quelle wie in
                // resolveLoginIdentifier (F-5) und startImpersonation.
                accounts: {
                    where: { providerId: 'credential' },
                    select: { password: true },
                },
            },
        });
        const credentialHash = user?.password
            ?? user?.accounts.find((account) => Boolean(account.password))?.password
            ?? null;
        // Konten ganz OHNE Passwort (Passkey-/Social-only) bleiben bewusst
        // `invalid`: ohne Passwort gibt es keinen Nachweis, dass der Anfragende
        // der Konto-Inhaber ist — eine Status-Auskunft wäre ein
        // Konto-Existenz-Orakel für jeden Besucher. Diesen Fall fängt der
        // Passkey-/Social-Pfad ab (login-form.tsx bzw. /auth/error).
        if (!user || user.deletedAt || !credentialHash) return ok({ status: 'invalid' });

        const passwordMatches = await bcrypt.compare(password, credentialHash);
        if (!passwordMatches) return ok({ status: 'invalid' });

        // Suspended takes precedence over unverified — admins suspending
        // a user is more meaningful than an unfinished verification.
        if (user.suspendedAt) return ok({ status: 'suspended' });

        const pendingMatch = user.pendingEmail?.trim().toLowerCase() === normalizedEmail;
        if (user.emailVerified && !pendingMatch) return ok({ status: 'verified' });

        const token = await prisma.emailVerificationToken.findFirst({
            where: { userId: user.id },
            orderBy: { createdAt: 'desc' },
        });
        if (!token) return ok({ status: 'unverified', tokenExpired: true });

        const tokenExpired = token.expiresAt < new Date();
        return ok({
            status: 'unverified',
            tokenExpired,
            expiresAt: token.expiresAt.toISOString(),
            pending: pendingMatch,
        });
    } catch (e) {
        console.error(e);
        void safeLogSystemError('Login probe failed', e, { email });
        return failure('Status konnte nicht geladen werden');
    }
}

/**
 * Löst eine Login-Eingabe (E-Mail ODER Benutzername) zur E-Mail auf, damit
 * der bestehende E-Mail-basierte Better-Auth-Login unverändert weiterläuft.
 *
 * - Enthält die Eingabe ein '@', wird sie als E-Mail behandelt und normalisiert
 *   (getrimmt + kleingeschrieben, konsistent zu getLoginProbe / Better-Auth)
 *   zurückgegeben — ohne DB-Zugriff. Die Passwortprüfung übernimmt hier der
 *   nachfolgende signIn.email; der bestehende E-Mail-Login bleibt unverändert.
 * - Andernfalls wird der Benutzername nachgeschlagen. Die E-Mail wird NUR
 *   zurückgegeben, wenn Benutzername UND Passwort gültig sind (bcrypt.compare
 *   gegen user.password, exakt wie in getLoginProbe). So ist die Action KEIN
 *   Username→E-Mail-Orakel mehr: ohne korrektes Passwort erfährt ein Angreifer
 *   nichts (weder Existenz noch PII); mit korrektem Passwort ist er ohnehin
 *   legitimiert. Die eigentliche Konto-Prüfung (Sperre, Verifizierung)
 *   übernimmt weiterhin der normale Login/getLoginProbe.
 *
 * Kein Enumerations-/PII-Leak: Ungültige Credentials ODER unbekannter/
 * gelöschter Benutzername liefern ununterscheidbar `{ email: null }`, sodass
 * der Login generisch fehlschlägt. Ratenbegrenzt (eigener Limiter).
 */
export async function resolveLoginIdentifier(
    identifier: string,
    password: string,
): Promise<ActionResult<{ email: string | null }>> {
    try {
        const raw = (identifier ?? '').trim();
        if (!raw) return ok({ email: null });

        // E-Mail-Eingabe: direkt normalisiert zurück (kein DB-Zugriff nötig).
        // Die Credential-Prüfung macht der nachfolgende signIn.email.
        if (raw.includes('@')) {
            return ok({ email: raw.toLowerCase() });
        }

        // Ohne Passwort gibt es für Benutzernamen bewusst keine Auflösung —
        // sonst wäre die Action wieder ein reines Enumerations-Orakel.
        if (!password) return ok({ email: null });

        // Benutzername → E-Mail. Rate-Limiting analog zu getLoginProbe, damit
        // diese Action kein Username-Enumerations-Orakel wird.
        const ip = await getRequestIp();
        const [{ success: idOk }, { success: ipOk }] = await Promise.all([
            loginResolveLimiter.limit(`login-resolve:${raw.toLowerCase()}`),
            loginResolveLimiter.limit(`login-resolve-ip:${ip}`),
        ]);
        if (!idOk || !ipOk) return ok({ email: null });

        // Case-insensitiver Match über die DB-Collation (utf8mb4_*_ci), exakt
        // wie die username-Uniqueness-Prüfung in der User-CRUD.
        const user = await prisma.user.findUnique({
            where: { username: raw },
            select: {
                email: true,
                deletedAt: true,
                password: true,
                // F-5: Better-Auth-Konten tragen ihr Passwort ggf. NUR als
                // credential-Account — user.password allein war eine
                // Username-Login-Regression für solche Konten.
                accounts: {
                    where: { providerId: 'credential' },
                    select: { password: true },
                },
            },
        });

        const credentialHash = user?.password
            ?? user?.accounts.find((account) => Boolean(account.password))?.password
            ?? null;

        // Gelöschte Konten haben username = null (Soft-Delete) und werden hier
        // gar nicht gefunden; der deletedAt-Guard ist Defense-in-Depth.
        if (!user || user.deletedAt || !user.email || !credentialHash) {
            return ok({ email: null });
        }

        // E-Mail (PII) NUR zurückgeben, wenn auch das Passwort stimmt — sonst
        // ununterscheidbar `null`. Suspendierte/unverifizierte Konten geben bei
        // korrektem Passwort BEWUSST ihre E-Mail zurück, damit der nachfolgende
        // getLoginProbe die passende "Konto deaktiviert"/"nicht bestätigt"-
        // Meldung liefern kann.
        const passwordMatches = await bcrypt.compare(password, credentialHash);
        if (!passwordMatches) return ok({ email: null });

        return ok({ email: user.email });
    } catch (e) {
        void safeLogSystemError('Login identifier resolve failed', e);
        // Bei Fehlern generisch null zurückgeben — kein Konto-Status-Leak.
        return ok({ email: null });
    }
}
